import { Injectable, inject } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ID } from '@core/ngrx-legacy';
import { TellentHttpApiService } from '@core/tellent-http-legacy';
import { TellentGroup, TellentGroupKind } from '@core/tellent-types';

import { GroupsParams, TellentGroupsResponse } from './tellent-groups.types';

@Injectable({ providedIn: 'root' })
export class TellentGroupsHttpClient {
  protected readonly httpApi = inject(TellentHttpApiService);

  public fetchGroups(params: GroupsParams): Observable<TellentGroupsResponse> {
    const { page, limit, query, sortBy, sortOrder, scope } = params;
    return this.httpApi.get('/teams', {
      page,
      limit,
      q: query,
      sort_by: sortBy,
      sort_dir: sortOrder,
      kind: scope,
    });
  }

  public addGroup(payload: {
    name: string;
    translations: Array<{ langCode: string; name: string }>;
    kind: TellentGroupKind;
  }): Observable<TellentGroup> {
    return this.httpApi.post('/teams', null, payload).pipe(map(data => data.team));
  }

  public removeGroup(payload: { guid: string }): Observable<void> {
    return this.httpApi.delete(`/teams/${payload.guid}`);
  }

  public fetchOne(payload: { id: ID }): Observable<TellentGroup> {
    return this.httpApi.get(`/teams/${payload.id}`).pipe(map(data => data.team));
  }

  public updateOne(payload: {
    guid: string;
    name: string;
    translations: Array<{ langCode: string; name: string }>;
  }): Observable<TellentGroup> {
    return this.httpApi
      .patch(`/teams/${payload.guid}`, null, {
        name: payload.name,
        translations: payload.translations,
      })
      .pipe(map(data => data.team));
  }

  public addMembers(payload: {
    groupGuid: string;
    membersGuids: string[];
  }): Observable<TellentGroup> {
    return this.manageMembers(payload.groupGuid, payload.membersGuids, 'assign');
  }

  public removeMembers(payload: {
    groupGuid: string;
    membersGuids: string[];
  }): Observable<TellentGroup> {
    return this.manageMembers(payload.groupGuid, payload.membersGuids, 'unassign');
  }

  private manageMembers(
    groupGuid: string,
    membersGuids: string[],
    action: 'assign' | 'unassign',
  ): Observable<TellentGroup> {
    return this.httpApi
      .patch(`/teams/${groupGuid}/${action}`, null, { membersGuids })
      .pipe(map(data => data.team));
  }
}
